import { CorporateCardRebateProgram } from "@/types/CardProgram";

export type RebateRateType = 'large_ticket' | 'level_iii' | 'volume_tier' | 'unmatched';

/** Why no rate could be determined. Machine-readable so the UI can explain itself. */
export type RebateUnmatchedReason =
  | 'no_active_program'
  | 'no_tiers_configured'
  | 'no_tier_matched';

/**
 * Result of a rebate calculation.
 *
 * `matched: false` means **we cannot say** what this supplier earns — no configured tier or
 * qualifier covers it. In that case `rebateRate` and `rebateAmount` are `null`, never `0`:
 * `0` is a legitimate answer (a genuinely configured 0% tier), and a `0` that means "unknown"
 * is indistinguishable from a `0` that means "nothing" once it reaches a total or a screen.
 *
 * See docs/units/R-CE2-REBATE-FALLBACK/plan.md — the SQL twin of this function used to
 * fabricate 2% here, producing 39% of all displayed rebate dollars from a rate nobody
 * configured.
 */
export interface RebateCalculationResult {
  matched: boolean;
  rebateRate: number | null;
  rebateAmount: number | null;
  rateType: RebateRateType;
  tierName: string | null;
  unmatchedReason?: RebateUnmatchedReason;
}

/** The single unmatched result. No rate, no dollars, and a reason. */
export function unmatchedRebate(reason: RebateUnmatchedReason): RebateCalculationResult {
  return {
    matched: false,
    rebateRate: null,
    rebateAmount: null,
    rateType: 'unmatched',
    tierName: null,
    unmatchedReason: reason,
  };
}

/**
 * Calculate rebate earnings for a supplier based on program configuration.
 *
 * Evaluation priority (highest to lowest):
 *   1. Large Ticket — VENDOR TRANSACTION QUALIFIER, not a tier. Qualifies when the
 *      vendor's average transaction exceeds the $ threshold AND passes L2/L3 data.
 *   2. Level III — VENDOR TRANSACTION QUALIFIER, not a tier. Qualifies when the
 *      vendor passes line-item (Level 2/3) transaction data.
 *   3. Volume Tier — PROGRAM-LEVEL tier based on the corporate's aggregate monthly spend.
 *
 * See: src/docs/specs/CARD_REBATE_STRUCTURE.md
 *
 * @param program              The active card rebate program configuration
 * @param annualVolumeAmount   PER-SUPPLIER annual card volume used for rebate dollar calc
 * @param monthlyVolumeForTier CORPORATE AGGREGATE enrolled monthly volume for tier lookup
 * @param hasLevelIIIData      Whether the vendor's acceptance profile confirms Level III capability
 * @param avgTransactionSize   Average per-transaction dollar amount (annual_volume / transaction_count).
 *                             Used for Large Ticket qualification — compares against `large_ticket_min_amount`.
 */
export function calculateRebateFromProgram(
  program: CorporateCardRebateProgram,
  annualVolumeAmount: number,
  monthlyVolumeForTier: number,
  hasLevelIIIData: boolean = false,
  avgTransactionSize?: number
): RebateCalculationResult {
  // Qualifier 1: Large Ticket — vendor transaction qualifier (NOT a tier).
  // Qualified by per-transaction size + enhanced data, NOT by annual volume.
  if (program.large_ticket_enabled && hasLevelIIIData &&
      program.large_ticket_min_amount != null &&
      program.large_ticket_rebate_rate != null &&
      avgTransactionSize != null && avgTransactionSize >= program.large_ticket_min_amount) {
    const rebateRate = program.large_ticket_rebate_rate;
    return { matched: true, rebateRate, rebateAmount: annualVolumeAmount * rebateRate, rateType: 'large_ticket', tierName: 'Large Ticket' };
  }

  // Qualifier 2: Level III — vendor transaction qualifier (NOT a tier).
  // Qualified by vendor's ability to pass enhanced L2/L3 transaction data.
  if (program.level_iii_enabled && hasLevelIIIData &&
      program.level_iii_rebate_rate != null) {
    const rebateRate = program.level_iii_rebate_rate;
    return { matched: true, rebateRate, rebateAmount: annualVolumeAmount * rebateRate, rateType: 'level_iii', tierName: 'Level III' };
  }

  // Fallback: Volume Tier — program-level tier based on corporate's aggregate monthly spend
  const existingTiers: Array<{ num: number; min: number; max: number | null; rate: number; name: string }> = [];

  for (let i = 1; i <= 10; i++) {
    const tierRate = program[`tier_${i}_rebate_rate` as keyof CorporateCardRebateProgram] as number;
    if (tierRate !== undefined && tierRate !== null) {
      existingTiers.push({
        num: i,
        min: (program[`tier_${i}_min_volume` as keyof CorporateCardRebateProgram] as number) || 0,
        max: (program[`tier_${i}_max_volume` as keyof CorporateCardRebateProgram] as number | null | undefined) ?? null,
        rate: tierRate,
        name: (program[`tier_${i}_name` as keyof CorporateCardRebateProgram] as string) || `Tier ${i}`
      });
    }
  }

  existingTiers.sort((a, b) => b.min - a.min);

  for (const tier of existingTiers) {
    if (monthlyVolumeForTier >= tier.min && (tier.max === null || monthlyVolumeForTier <= tier.max)) {
      return { matched: true, rebateRate: tier.rate, rebateAmount: annualVolumeAmount * tier.rate, rateType: 'volume_tier', tierName: tier.name };
    }
  }

  // Nothing matched. Do NOT invent a rate and do NOT report $0 — say so, and say why.
  // A ladder can fail to cover a volume two ways, and both are live today:
  //   - a GAP between tiers (corporate 1: 375k → 700k; corporate 440: 250k → 1M)
  //   - an INVERTED tier where min > max, which can never match (corporate 440's Tier 3)
  return unmatchedRebate(
    existingTiers.length === 0 ? 'no_tiers_configured' : 'no_tier_matched'
  );
}

/**
 * Look up the rebate rate for a given tier name or transaction qualifier name.
 * Accepts volume tier names (e.g., "Bronze", "Tier 1") and transaction qualifier
 * names ("Level 3", "Level III", "Large Ticket").
 */
export function getRebateRateForTier(program: CorporateCardRebateProgram, tierName: string | null | undefined): number | null {
  if (!tierName) return null;

  // Vendor transaction qualifiers (not tiers)
  if (tierName === 'Level 3' || tierName === 'Level III') {
    return program.level_iii_enabled ? (program.level_iii_rebate_rate ?? null) : null;
  }
  if (tierName === 'Large Ticket') {
    return program.large_ticket_enabled ? (program.large_ticket_rebate_rate ?? null) : null;
  }

  // Volume tiers

  for (let i = 1; i <= 10; i++) {
    const name = program[`tier_${i}_name` as keyof CorporateCardRebateProgram] as string;
    if (name && name === tierName) {
      return (program[`tier_${i}_rebate_rate` as keyof CorporateCardRebateProgram] as number) ?? null;
    }
  }

  return null;
}

/**
 * Get all configured rebate category names from a program.
 * Returns both volume tier names AND vendor transaction qualifier names.
 * Note: "Level 3" and "Large Ticket" are transaction qualifiers, not tiers —
 * they are included here for backward compatibility with UI components that
 * need a flat list of all possible rebate rate sources.
 */
export function getConfiguredTierNames(program: CorporateCardRebateProgram): string[] {
  // Volume tiers (program-level, based on aggregate corporate spend)
  const names: string[] = [];
  for (let i = 1; i <= 10; i++) {
    const name = program[`tier_${i}_name` as keyof CorporateCardRebateProgram] as string;
    if (name) names.push(name);
  }
  // Vendor transaction qualifiers (NOT tiers — per-vendor/per-transaction factors)
  if (program.level_iii_enabled && program.level_iii_rebate_rate != null) names.push('Level 3');
  if (program.large_ticket_enabled && program.large_ticket_rebate_rate != null) names.push('Large Ticket');
  return names;
}

/** Get current tier name for a given monthly volume */
export function getCurrentTierName(program: CorporateCardRebateProgram, monthlyVolume: number): string | null {
  for (let i = 10; i >= 1; i--) {
    const tierName = program[`tier_${i}_name` as keyof CorporateCardRebateProgram] as string;
    const tierMin = program[`tier_${i}_min_volume` as keyof CorporateCardRebateProgram] as number;
    const tierMax = program[`tier_${i}_max_volume` as keyof CorporateCardRebateProgram] as number;

    if (tierName && tierMin !== undefined) {
      if (monthlyVolume >= tierMin && (tierMax === null || monthlyVolume <= tierMax)) {
        return tierName;
      }
    }
  }
  return null;
}
